import merge from "lodash/merge";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { convertDotSeparatedStringToJSONObject, convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import { DEFAULT_EMAIL_TEMPLATE, EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME, emailTemplateContentStringRegex, REQUIRED_EMAIL_TEMPLATE_VARIABLES } from "@constants/email-template-constants";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { IDWSession } from "@idw-types/data/idw-session";
import { FormattedEmailTemplate } from "@idw-types/data/email-template";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { isAxnAdminUser } from "@utils/permissions-util";
import { resources } from "@constants/hardcoded-strings";


export const getInitialEmailTemplate = (
    axnCompanies: Array<AxnCompany>, isVericationServiceDetailsPage: boolean,
    session: IDWSession,
): FormattedEmailTemplate => {


    const initialEmailTemplate: FormattedEmailTemplate = {
        template: DEFAULT_EMAIL_TEMPLATE,
        name: "",
        company: null,
        id: null,
        uuid: null,
        rpServiceID: null,
        sender: resources.pages.emailTemplateDetails.tabs.variables.defaultSender.title,
        status: EntityStatusTypes.ACTIVE,
        languages: { en: {}},
        templateAvailabilityLevel: null,
        permissions: {
            canCreate: true,
            canModify: true,
            canView: true
        },
    };

    let company: AxnCompany = null;
    const templateAvailabilityLevel = getDefaultTemplateAvailabilityLevel(session, isVericationServiceDetailsPage);

    if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.ORGANIZATION) {
        company = axnCompanies[0];
    }

    return {
        ...initialEmailTemplate,
        templateAvailabilityLevel,
        company,
    }
}
const getDefaultTemplateAvailabilityLevel = (session: IDWSession, isVericationServiceDetailsPage: boolean) => {
    const isAdminUser = isAxnAdminUser(session.user);
    if (isAdminUser) {
        return isVericationServiceDetailsPage ? TemplateAvailabilityLevelTypes.ORGANIZATION : TemplateAvailabilityLevelTypes.GLOBAL;
    } else {
        return TemplateAvailabilityLevelTypes.ORGANIZATION;
    }
};

export const getVariablesFromEmailTemplate = (template: string): { name: string; value: string; }[] => {
    const matches = template.match(emailTemplateContentStringRegex);
    const variables: Array<{ name: string; value: string; }> = [];
    if (matches) {
        matches.forEach(match => {
            const splitVar = match.split(/!(.*)/s);
            if (splitVar && splitVar.length > 1) {
                const variableName = splitVar[0].substring(3, splitVar[0].length - 1);
                const variableValue = splitVar[1].substring(1, splitVar[1].length - 2);

                variables.push({
                    name: variableName,
                    value: variableValue
                });
            }
        });
    }

    return variables;
};

export const getEmailTemplateVariables = (languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>>, template: string, selectedLanguage: LocaleTypes) => {
    const variables = getVariablesFromEmailTemplate(template);
    let flattenedLanguageFile: { [key: string]: string } = {};

    if (languageFiles[selectedLanguage]) {
        flattenedLanguageFile = convertJSONObjectToDotSeparatedString(languageFiles[selectedLanguage]);
    }

    const finalVariables: Array<{ name: string; value: string; }> = [];
    variables.forEach(variable => {
        const { name, value } = variable;
        const isPropertyInLanguageFile = Object.keys(flattenedLanguageFile).includes(name);
        let updatedValue;
        if (!REQUIRED_EMAIL_TEMPLATE_VARIABLES.includes(name)) {
            if (!isPropertyInLanguageFile && value) {// the variable doesn't exist in language files and we have a default value
                updatedValue = value;
            } else if (isPropertyInLanguageFile) {// the variable already exist in language files empty or not 
                updatedValue = flattenedLanguageFile[name];
            } else {
                updatedValue = value || "";
            }

            finalVariables.push({ name, value: updatedValue });
        }
    });

    return finalVariables;
}

export const removeUnusedVariablesFromEmailTemplateLanguageFiles = (
    template: string,
    languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any; }>>
) => {
    const variables = getVariablesFromEmailTemplate(template);
    const localeToVariablesMap: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = {};

    variables.push({ name: EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME, value: DEFAULT_EMAIL_TEMPLATE });

    //remove any variables that aren't in template (excluding required ones)
    (Object.keys(languageFiles) as Array<LocaleTypes>).forEach(locale => {
        const flattenedLanguageFile = convertJSONObjectToDotSeparatedString(languageFiles[locale]);
        let finalLanguageFile: { [key: string]: any } = {};
        variables.forEach(variable => {
            if (flattenedLanguageFile[variable.name]) {
                const variableJSON = convertDotSeparatedStringToJSONObject(variable.name, flattenedLanguageFile[variable.name]);
                finalLanguageFile = merge({}, finalLanguageFile, variableJSON);
            } else {
                const variableJSON = convertDotSeparatedStringToJSONObject(variable.name, variable.value);
                finalLanguageFile = merge({}, finalLanguageFile, variableJSON);
            }
        });
        localeToVariablesMap[locale] = finalLanguageFile;
    });
    return localeToVariablesMap;
};

export const getUnformattedLanguageFiles = (languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>>): Partial<MultiLanguageWrapper<string>> => {
    return (Object.keys(languageFiles || {}) as Array<LocaleTypes>).reduce((result, locale) => {
        const localeString = JSON.stringify(languageFiles[locale]) || "";
        result[locale] = localeString;
        return result;

    }, {} as Partial<MultiLanguageWrapper<string>>);
};